// @ts-check
// Regras puras da paridade do kanban e da tabela (docs/arquitetura-appcrm/08-contrato-kanban-tabela.md; doc 06,
// seções 4.5, 5.1, 5.5, 5.6, 6 e 9): agregações por tipo de campo, larguras e ordem de colunas, grupos (colunas do
// kanban e seções da tabela), campos do cartão, limpar célula pelo teclado, seleção em intervalo e mudança de etapa
// de vários cards, um por vez. Sem DOM nem estado global, para poder ser testado no runtime da Cloudflare.
import { getFieldTypeDefinition } from '../../../../shared/field-types/field-type-definitions.js';
import { isHiddenBoardStage } from '../../../../shared/metadata/funnel-stages.js';
import {
  FIELD_TYPE, getEmptyFieldValue, getLabelIdentifierField, getRelationIdFieldName, isFieldDisplayable, isFieldValueEmpty, isManyToOneRelation,
} from '../../lib/field-metadata.js';
import { buildSubFieldClearPatch, isSubFieldProjection, projectSubField } from '../../lib/address-sub-fields.js';
import { columnSubFieldNamesOf, defaultSubFieldColumnSize } from '../../../../shared/views/sub-field-columns.js';

/**
 * @typedef {import('../../lib/field-metadata.js').FieldMetadata} FieldMetadata
 * @typedef {import('../../data/metadata-store.js').View} View
 * @typedef {View['viewFields'][number]} ViewField
 * @typedef {{ id?: string, fieldValue: string, isVisible?: boolean, position?: number }} ViewGroup
 * @typedef {{ operation: string, fieldMetadataId: string | null, subFieldName?: string | null }} KanbanAggregate
 * @typedef {{ key: string, value: string | null, isVisible: boolean, position: number }} RecordGroup
 * @typedef {{ apiPatch: Record<string, unknown>, localPatch: Record<string, unknown> } | null} FieldPatch
 * @typedef {import('../../lib/field-metadata.js').ObjectMetadata} ObjectMetadata
 * @typedef {{ field: FieldMetadata, width: number, viewFieldId: string | null, isTrashColumn?: boolean, subFieldName?: string | null,
 *   aggregateOperation?: string | null }} TableColumn
 *   subFieldName: parte do endereço numa viewField com subFieldColumns (null é o endereço inteiro); ausente nas colunas comuns
 */

export const TABLE_COLUMN_MIN_WIDTH = 104;
export const TABLE_COLUMN_DEFAULT_WIDTH = 150;
export const KANBAN_COLUMN_MIN_WIDTH = 150;
export const KANBAN_COLUMN_MAX_WIDTH = 400;
export const KANBAN_COLUMN_DEFAULT_WIDTH = 200;
// o motor aceita até 5 agregações por pedido (rota groupBy) e por cabeçalho do kanban
export const MAXIMUM_AGGREGATES_PER_REQUEST = 5;
export const MAXIMUM_KANBAN_AGGREGATES = 5;
export const NO_VALUE_GROUP_KEY = '__no_value__';

// grupos do menu de agregação, na ordem do Twenty (contagem, percentual, numéricas)
export const AGGREGATE_OPERATION_GROUPS = Object.freeze([
  Object.freeze({ key: 'count', operations: Object.freeze(['COUNT', 'COUNT_EMPTY', 'COUNT_NOT_EMPTY', 'COUNT_UNIQUE_VALUES', 'COUNT_TRUE', 'COUNT_FALSE']) }),
  Object.freeze({ key: 'percentage', operations: Object.freeze(['PERCENTAGE_EMPTY', 'PERCENTAGE_NOT_EMPTY']) }),
  Object.freeze({ key: 'numeric', operations: Object.freeze(['SUM', 'AVG', 'MIN', 'MAX']) }),
]);
const PERCENTAGE_OPERATIONS = new Set(['PERCENTAGE_EMPTY', 'PERCENTAGE_NOT_EMPTY']);
const COUNT_OPERATIONS = new Set(['COUNT', 'COUNT_EMPTY', 'COUNT_NOT_EMPTY', 'COUNT_UNIQUE_VALUES', 'COUNT_TRUE', 'COUNT_FALSE']);
// somar, mediar e achar mínimo e máximo só fazem sentido no valor; nas datas, mínimo e máximo são a mais antiga e a mais recente
const VALUE_OPERATIONS = new Set(['SUM', 'AVG', 'MIN', 'MAX']);

/**
 * Operações de agregação que o campo aceita: relações só contam, os demais seguem a definição do tipo.
 * @param {FieldMetadata} field
 * @returns {string[]}
 */
export function getAvailableAggregateOperations(field) {
  if (field.type === FIELD_TYPE.RELATION) return isManyToOneRelation(field) ? ['COUNT'] : [];
  const allowed = new Set(['COUNT', ...getFieldTypeDefinition(field.type).aggregateOperations]);
  return AGGREGATE_OPERATION_GROUPS.flatMap((group) => group.operations.filter((operation) => allowed.has(operation)));
}

/** @param {string} operation */
export const isPercentageAggregate = (operation) => PERCENTAGE_OPERATIONS.has(operation);
/** @param {string} operation */
export const isCountAggregate = (operation) => COUNT_OPERATIONS.has(operation);
/** Operações cujo resultado está na unidade do campo (moeda, número, data). @param {string} operation */
export const isValueAggregate = (operation) => VALUE_OPERATIONS.has(operation);

/**
 * Sub-campo agregado: o valor da moeda; nos outros compostos o motor escolhe o sub-campo padrão.
 * @param {FieldMetadata} field
 */
export function getAggregateSubFieldName(field) {
  return field.type === FIELD_TYPE.CURRENCY ? 'amountMicros' : null;
}

/**
 * Item do parâmetro `aggregate` da rota groupBy, como `SUM:amount.amountMicros`.
 * @param {FieldMetadata} field @param {string} operation @param {string | null} [subFieldName]
 */
export function buildAggregateParameter(field, operation, subFieldName = getAggregateSubFieldName(field)) {
  return `${operation}:${subFieldName ? `${field.name}.${subFieldName}` : field.name}`;
}

/**
 * Divide uma lista em pedaços de no máximo `size` itens (agregações por pedido).
 * @template TItem
 * @param {TItem[]} items @param {number} size
 * @returns {TItem[][]}
 */
export function chunkList(items, size) {
  /** @type {TItem[][]} */
  const chunks = [];
  for (let index = 0; index < items.length; index += size) chunks.push(items.slice(index, index + size));
  return chunks;
}

/** @param {number} value @param {number} minimum @param {number} maximum */
const clamp = (value, minimum, maximum) => Math.min(maximum, Math.max(minimum, value));

/** Largura de coluna da tabela ao redimensionar: inteira e nunca abaixo de 104 px. @param {number} width */
export const clampTableColumnWidth = (width) => Math.max(TABLE_COLUMN_MIN_WIDTH, Math.round(width));

/** Largura das colunas do kanban: inteira, entre 150 e 400 px. @param {number} width */
export const clampKanbanColumnWidth = (width) => clamp(Math.round(width), KANBAN_COLUMN_MIN_WIDTH, KANBAN_COLUMN_MAX_WIDTH);

/**
 * Atualiza (ou cria) o viewField de um campo. A coluna identificadora pode ainda não ter viewField.
 * @param {ViewField[]} viewFields @param {string} fieldMetadataId @param {Partial<ViewField>} changes
 * @returns {ViewField[]}
 */
export function upsertViewField(viewFields, fieldMetadataId, changes) {
  if (viewFields.some((viewField) => viewField.fieldMetadataId === fieldMetadataId)) {
    return viewFields.map((viewField) => (viewField.fieldMetadataId === fieldMetadataId ? { ...viewField, ...changes } : viewField));
  }
  const minimumPosition = Math.min(0, ...viewFields.map((viewField) => viewField.position));
  return [...viewFields, {
    id: globalThis.crypto.randomUUID(), fieldMetadataId, position: minimumPosition - 1, isVisible: true, size: TABLE_COLUMN_DEFAULT_WIDTH, ...changes,
  }];
}

/**
 * Nova ordem das colunas visíveis (ids de campo na ordem exibida); as ocultas vêm depois, na ordem que tinham.
 * @param {ViewField[]} viewFields @param {string[]} orderedFieldIds
 * @returns {ViewField[]}
 */
export function applyViewFieldOrder(viewFields, orderedFieldIds) {
  const rank = new Map(orderedFieldIds.map((fieldId, index) => [fieldId, index]));
  const rest = [...viewFields].filter((viewField) => !rank.has(viewField.fieldMetadataId)).sort((first, second) => first.position - second.position);
  const restRank = new Map(rest.map((viewField, index) => [viewField.fieldMetadataId, orderedFieldIds.length + index]));
  return viewFields.map((viewField) => ({ ...viewField, position: rank.get(viewField.fieldMetadataId) ?? restRank.get(viewField.fieldMetadataId) ?? viewField.position }));
}

/**
 * Move um item de uma lista para antes do item alvo (ou para o fim, sem alvo).
 * @param {string[]} ids @param {string} movedId @param {string | null} beforeId
 */
export function moveIdBefore(ids, movedId, beforeId) {
  const without = ids.filter((id) => id !== movedId);
  const targetIndex = beforeId ? without.indexOf(beforeId) : -1;
  if (targetIndex < 0) return [...without, movedId];
  return [...without.slice(0, targetIndex), movedId, ...without.slice(targetIndex)];
}

/**
 * Troca um item de lugar com o vizinho (`offset` -1 ou 1); fora dos limites, nada muda.
 * @param {string[]} ids @param {string} movedId @param {number} offset
 */
export function moveIdBy(ids, movedId, offset) {
  const index = ids.indexOf(movedId);
  const target = index + offset;
  if (index < 0 || target < 0 || target >= ids.length) return ids;
  const next = [...ids];
  [next[index], next[target]] = [next[target], next[index]];
  return next;
}

/**
 * Grupos de um campo SELECT na ordem da view: um por opção e "Sem valor" quando o campo aceita vazio. Opção sem
 * viewGroup aparece, menos as etapas escondidas do quadro (Não é lead), que só aparecem se a view pedir.
 * @param {ViewGroup[]} viewGroups @param {FieldMetadata} groupField
 * @returns {RecordGroup[]}
 */
export function getRecordGroups(viewGroups, groupField) {
  const byValue = new Map(viewGroups.map((group) => [group.fieldValue, group]));
  const options = [...(groupField.options ?? [])].sort((first, second) => (first.position ?? 0) - (second.position ?? 0));
  /** @type {RecordGroup[]} */
  const groups = options.map((option, index) => {
    const saved = byValue.get(option.value);
    return {
      key: option.value,
      value: option.value,
      isVisible: saved ? saved.isVisible !== false : !isHiddenBoardStage(option.value),
      position: saved?.position ?? option.position ?? index,
    };
  });
  groups.sort((first, second) => first.position - second.position);
  if (groupField.isNullable !== false) {
    const saved = byValue.get('');
    groups.push({ key: NO_VALUE_GROUP_KEY, value: null, isVisible: saved ? saved.isVisible !== false : true, position: saved?.position ?? Number.MAX_SAFE_INTEGER });
  }
  return groups;
}

/**
 * viewGroups completos a partir dos grupos (todas as opções, posições 0..n), para gravar na view.
 * @param {RecordGroup[]} groups
 * @returns {ViewGroup[]}
 */
export function toViewGroups(groups) {
  return groups.map((group, index) => ({ fieldValue: group.value ?? '', isVisible: group.isVisible, position: index }));
}

/**
 * Mostra ou oculta um grupo.
 * @param {ViewGroup[]} viewGroups @param {FieldMetadata} groupField @param {string} key @param {boolean} isVisible
 */
export function setGroupVisibility(viewGroups, groupField, key, isVisible) {
  return toViewGroups(getRecordGroups(viewGroups, groupField).map((group) => (group.key === key ? { ...group, isVisible } : group)));
}

/**
 * Move um grupo para a esquerda ou a direita entre os visíveis; os ocultos ficam onde estavam.
 * @param {ViewGroup[]} viewGroups @param {FieldMetadata} groupField @param {string} key @param {number} offset
 */
export function moveGroup(viewGroups, groupField, key, offset) {
  const groups = getRecordGroups(viewGroups, groupField);
  const visibleKeys = groups.filter((group) => group.isVisible).map((group) => group.key);
  const movedKeys = moveIdBy(visibleKeys, key, offset);
  const byKey = new Map(groups.map((group) => [group.key, group]));
  let visibleIndex = 0;
  const ordered = groups.map((group) => {
    if (!group.isVisible) return group;
    const replacement = /** @type {RecordGroup} */ (byKey.get(movedKeys[visibleIndex]));
    visibleIndex += 1;
    return replacement;
  });
  return toViewGroups(ordered);
}

/**
 * Grupos ocultos que a pessoa pode voltar a mostrar: as etapas escondidas do quadro (Não é lead) ficam de fora.
 * @param {ViewGroup[]} viewGroups @param {FieldMetadata} groupField
 */
export function getRestorableHiddenGroups(viewGroups, groupField) {
  return getRecordGroups(viewGroups, groupField).filter((group) => !group.isVisible && !(group.value && isHiddenBoardStage(group.value)));
}

/**
 * Grupos exibidos: os visíveis e, com "Ocultar vazios", só os que têm registros (ou estão na lista de mantidos).
 * @param {RecordGroup[]} groups @param {(key: string) => number | undefined} countOf @param {boolean} shouldHideEmptyGroups
 * @param {Set<string>} [keepKeys]
 */
export function getDisplayedGroups(groups, countOf, shouldHideEmptyGroups, keepKeys = new Set()) {
  return groups.filter((group) => group.isVisible && (!shouldHideEmptyGroups || keepKeys.has(group.key) || (countOf(group.key) ?? 1) > 0));
}

/**
 * Colunas do quadro com o escopo de etapas do papel. Na etapa do card, só as de Ver; as de Pode passar viram colunas de
 * entrega no fim (sem cards nem agregados, fora de "Ocultar vazios" e dos viewGroups) e Não é lead fica com a área
 * própria de soltar. Na etapa do aluno, as colunas vêm dos grupos com registros: a etapa fora de Ver só aparece com
 * aluno visível nela (o card dele está em Ver).
 * @param {RecordGroup[]} groups grupos da view, já na ordem
 * @param {{ kind: 'CARD' | 'CHILD' | null, visibleStages: Set<string> | null, handoffStages?: string[], countOf?: (key: string) => number | undefined }} scope
 * @returns {{ groups: RecordGroup[], handoffValues: string[] }}
 */
export function applyStageScopeToBoardGroups(groups, { kind, visibleStages, handoffStages = [], countOf = () => undefined }) {
  if (!kind || !visibleStages) return { groups, handoffValues: [] };
  const isShown = (/** @type {RecordGroup} */ group) => {
    if (group.value === null || visibleStages.has(group.value)) return true;
    return kind === 'CHILD' && (countOf(group.key) ?? 0) > 0;
  };
  const handoffValues = kind === 'CARD' ? handoffStages.filter((value) => !visibleStages.has(value) && !isHiddenBoardStage(value)) : [];
  return { groups: groups.filter(isShown), handoffValues };
}

/**
 * Campos do cartão do kanban: os visíveis na ordem da view e os ocultos disponíveis, sem o título nem o agrupamento.
 * @param {ViewField[]} viewFields @param {FieldMetadata[]} candidateFields @param {Set<string>} excludedFieldIds
 */
export function getCardFieldLists(viewFields, candidateFields, excludedFieldIds) {
  const candidates = new Map(candidateFields.filter((field) => !excludedFieldIds.has(field.id)).map((field) => [field.id, field]));
  const visible = [...viewFields]
    .filter((viewField) => viewField.isVisible && candidates.has(viewField.fieldMetadataId))
    .sort((first, second) => first.position - second.position)
    .map((viewField) => /** @type {FieldMetadata} */ (candidates.get(viewField.fieldMetadataId)));
  const visibleIds = new Set(visible.map((field) => field.id));
  const hidden = [...candidates.values()].filter((field) => !visibleIds.has(field.id));
  return { visible, hidden };
}

/**
 * Mostra ou oculta um campo da view; mostrar põe o campo no fim, como o Twenty.
 * @param {ViewField[]} viewFields @param {string} fieldMetadataId @param {boolean} isVisible
 */
export function setViewFieldVisibility(viewFields, fieldMetadataId, isVisible) {
  if (!isVisible) return viewFields.map((viewField) => (viewField.fieldMetadataId === fieldMetadataId ? { ...viewField, isVisible: false } : viewField));
  const lastPosition = Math.max(0, ...viewFields.map((viewField) => viewField.position));
  const existing = viewFields.find((viewField) => viewField.fieldMetadataId === fieldMetadataId);
  if (existing) return viewFields.map((viewField) => (viewField.fieldMetadataId === fieldMetadataId ? { ...viewField, isVisible: true, position: lastPosition + 1 } : viewField));
  return [...viewFields, { id: globalThis.crypto.randomUUID(), fieldMetadataId, position: lastPosition + 1, isVisible: true, size: 100 }];
}

/**
 * Agregação nova no cabeçalho do kanban, sem repetir e até o limite.
 * @param {KanbanAggregate[]} aggregates @param {KanbanAggregate} aggregate
 */
export function addKanbanAggregate(aggregates, aggregate) {
  const isDuplicate = aggregates.some((item) => item.operation === aggregate.operation && (item.fieldMetadataId ?? null) === (aggregate.fieldMetadataId ?? null)
    && (item.subFieldName ?? null) === (aggregate.subFieldName ?? null));
  if (isDuplicate || aggregates.length >= MAXIMUM_KANBAN_AGGREGATES) return aggregates;
  return [...aggregates, { operation: aggregate.operation, fieldMetadataId: aggregate.fieldMetadataId ?? null, subFieldName: aggregate.subFieldName ?? null }];
}

/** @param {KanbanAggregate[]} aggregates @param {number} index */
export const removeKanbanAggregate = (aggregates, index) => aggregates.filter((_aggregate, candidateIndex) => candidateIndex !== index);

const DEFAULT_TABLE_COLUMN_WIDTH = 150;
const LABEL_IDENTIFIER_COLUMN_WIDTH = 210;

/**
 * Colunas de uma viewField de endereço mostrada em partes (shared/views/sub-field-columns.js), na ordem da lista; a
 * parte null é o endereço inteiro, com o próprio campo. Sem partes (ou campo de outro tipo), null.
 * @param {ViewField} viewField @param {FieldMetadata} field
 * @returns {Array<{ field: FieldMetadata, width: number, subFieldName: string | null, aggregateOperation: string | null }> | null}
 */
export function expandSubFieldColumns(viewField, field) {
  const parts = viewField.subFieldColumns;
  if (!Array.isArray(parts) || parts.length === 0) return null;
  const allowed = columnSubFieldNamesOf(field.type);
  if (allowed.length === 0) return null;
  return parts
    .filter((part) => part.subFieldName === null || part.subFieldName === undefined || allowed.includes(part.subFieldName))
    .map((part) => ({
      field: part.subFieldName ? projectSubField(field, part.subFieldName) : field,
      width: part.size || defaultSubFieldColumnSize(part.subFieldName),
      subFieldName: part.subFieldName ?? null,
      aggregateOperation: part.aggregateOperation ?? null,
    }));
}

/**
 * Colunas visíveis na ordem da view; o identificador de rótulo é sempre a primeira. Endereço com partes vira uma coluna
 * por parte (CEP, Bairro, Cidade). Campo que o papel não lê já saiu dos metadados e não vira coluna.
 * @param {ObjectMetadata} objectMetadata @param {View} view
 * @returns {TableColumn[]}
 */
export function getTableColumns(objectMetadata, view) {
  const fieldsById = new Map(objectMetadata.fields.map((field) => [field.id, field]));
  const labelField = getLabelIdentifierField(objectMetadata);
  /** @type {TableColumn[]} */
  const columns = [...view.viewFields]
    .filter((viewField) => viewField.isVisible && fieldsById.has(viewField.fieldMetadataId))
    .sort((first, second) => first.position - second.position)
    .flatMap((viewField) => {
      const field = /** @type {FieldMetadata} */ (fieldsById.get(viewField.fieldMetadataId));
      const parts = expandSubFieldColumns(viewField, field);
      if (parts) return parts.map((part) => ({ ...part, viewFieldId: viewField.id }));
      return [{ field, width: viewField.size || DEFAULT_TABLE_COLUMN_WIDTH, viewFieldId: viewField.id, aggregateOperation: viewField.aggregateOperation ?? null }];
    })
    .filter((column) => isFieldDisplayable(column.field));
  if (labelField) {
    const labelIndex = columns.findIndex((column) => column.field.id === labelField.id);
    const [labelColumn] = labelIndex >= 0 ? columns.splice(labelIndex, 1) : [{ field: labelField, width: LABEL_IDENTIFIER_COLUMN_WIDTH, viewFieldId: null, aggregateOperation: null }];
    columns.unshift(labelColumn);
  }
  if (columns.length === 0) {
    return objectMetadata.fields.filter((field) => isFieldDisplayable(field) && !field.isSystem).slice(0, 6)
      .map((field) => ({ field, width: DEFAULT_TABLE_COLUMN_WIDTH, viewFieldId: null, aggregateOperation: null }));
  }
  return columns;
}

/** Id de coluna: o do campo, ou campo.parte numa coluna de sub-campo. @param {TableColumn} column */
export const tableColumnIdOf = (column) => column.field.id;

/** @param {string} columnId @returns {{ fieldId: string, subFieldName: string | null }} */
function parseColumnId(columnId) {
  const separator = columnId.indexOf('.');
  return separator < 0 ? { fieldId: columnId, subFieldName: null } : { fieldId: columnId.slice(0, separator), subFieldName: columnId.slice(separator + 1) };
}

/**
 * Nova ordem das colunas (ids na ordem exibida, partes como campo.parte): a posição de cada viewField é a da sua primeira
 * coluna e as partes seguem a ordem dos ids. Por isso arrastar uma parte para fora do bloco só a reordena dentro dele.
 * @param {ViewField[]} viewFields @param {string[]} orderedColumnIds
 * @returns {ViewField[]}
 */
export function applyColumnOrder(viewFields, orderedColumnIds) {
  /** @type {string[]} */
  const orderedFieldIds = [];
  /** @type {Map<string, Array<string | null>>} */
  const partsByField = new Map();
  for (const columnId of orderedColumnIds) {
    const { fieldId, subFieldName } = parseColumnId(columnId);
    if (!partsByField.has(fieldId)) {
      orderedFieldIds.push(fieldId);
      partsByField.set(fieldId, []);
    }
    partsByField.get(fieldId)?.push(subFieldName);
  }
  return applyViewFieldOrder(viewFields, orderedFieldIds).map((viewField) => {
    const parts = viewField.subFieldColumns;
    const order = partsByField.get(viewField.fieldMetadataId);
    if (!Array.isArray(parts) || parts.length < 2 || !order) return viewField;
    const rank = new Map(order.map((subFieldName, index) => [subFieldName ?? '', index]));
    const rankOf = (/** @type {{ subFieldName: string | null }} */ part) => rank.get(part.subFieldName ?? '') ?? order.length;
    return { ...viewField, subFieldColumns: [...parts].sort((first, second) => rankOf(first) - rankOf(second)) };
  });
}

/**
 * Mostra ou oculta uma parte do endereço. Ocultar a última parte oculta o campo; mostrar uma parte de um campo fora da
 * view (ou oculto) cria a viewField só com ela; mostrar uma parte de um endereço inteiro mantém o inteiro e soma a parte.
 * subFieldName null é o endereço inteiro.
 * @param {ViewField[]} viewFields @param {string} fieldId @param {string | null} subFieldName @param {boolean} isVisible
 * @returns {ViewField[]}
 */
export function setSubFieldColumnVisibility(viewFields, fieldId, subFieldName, isVisible) {
  const part = { subFieldName, size: defaultSubFieldColumnSize(subFieldName), aggregateOperation: null };
  const existing = viewFields.find((viewField) => viewField.fieldMetadataId === fieldId);
  const replace = (/** @type {Partial<ViewField>} */ changes) =>
    viewFields.map((viewField) => (viewField.fieldMetadataId === fieldId ? { ...viewField, ...changes } : viewField));
  const parts = existing?.subFieldColumns ?? null;
  if (!isVisible) {
    if (!existing) return viewFields;
    if (!parts) return subFieldName === null ? replace({ isVisible: false }) : viewFields;
    const remaining = parts.filter((candidate) => (candidate.subFieldName ?? null) !== subFieldName);
    if (remaining.length === 0) return replace({ isVisible: false, subFieldColumns: null });
    // só o endereço inteiro sobrou: volta a ser uma coluna comum, com a largura dela
    if (remaining.length === 1 && remaining[0].subFieldName === null) return replace({ subFieldColumns: null, size: remaining[0].size });
    return replace({ subFieldColumns: remaining });
  }
  const lastPosition = Math.max(0, ...viewFields.map((viewField) => viewField.position));
  if (!existing) {
    return [...viewFields, {
      id: globalThis.crypto.randomUUID(), fieldMetadataId: fieldId, position: lastPosition + 1, isVisible: true,
      size: defaultSubFieldColumnSize(null), ...(subFieldName === null ? {} : { subFieldColumns: [part] }),
    }];
  }
  if (!existing.isVisible) return replace({ isVisible: true, position: lastPosition + 1, subFieldColumns: subFieldName === null ? null : [part] });
  if (!parts) return subFieldName === null ? viewFields : replace({ subFieldColumns: [{ subFieldName: null, size: existing.size, aggregateOperation: existing.aggregateOperation ?? null }, part] });
  if (parts.some((candidate) => (candidate.subFieldName ?? null) === subFieldName)) return viewFields;
  return replace({ subFieldColumns: [...parts, part] });
}

/**
 * Largura ou agregação de uma parte; numa viewField sem partes (subFieldName null), do próprio campo.
 * @param {ViewField[]} viewFields @param {string} fieldId @param {string | null} subFieldName
 * @param {{ size?: number, aggregateOperation?: string | null }} changes
 * @returns {ViewField[]}
 */
export function updateSubFieldColumn(viewFields, fieldId, subFieldName, changes) {
  const existing = viewFields.find((viewField) => viewField.fieldMetadataId === fieldId);
  if (!existing?.subFieldColumns) return subFieldName === null ? upsertViewField(viewFields, fieldId, changes) : viewFields;
  return viewFields.map((viewField) => (viewField.fieldMetadataId === fieldId
    ? { ...viewField, subFieldColumns: (viewField.subFieldColumns ?? []).map((part) => ((part.subFieldName ?? null) === subFieldName ? { ...part, ...changes } : part)) }
    : viewField));
}

// o nome do registro e os campos sem "vazio" (sim/não) não se limpam pelo teclado
const UNCLEARABLE_FIELD_TYPES = new Set([FIELD_TYPE.BOOLEAN, FIELD_TYPE.FULL_NAME, FIELD_TYPE.RICH_TEXT]);

/**
 * Patch que esvazia o campo (Delete/Backspace na célula focada), ou null quando o campo não pode ser limpo ou já
 * está vazio. Quem chama confere antes se o campo é somente leitura ou está travado pela etapa.
 * @param {FieldMetadata} field @param {Record<string, any>} record
 * @returns {FieldPatch}
 */
export function buildClearFieldPatch(field, record) {
  // parte do endereço (CEP, bairro...): limpa só ela
  if (isSubFieldProjection(field)) return buildSubFieldClearPatch(field, record);
  if (UNCLEARABLE_FIELD_TYPES.has(field.type) || field.isNullable === false) return null;
  if (field.type === FIELD_TYPE.RELATION) {
    if (!isManyToOneRelation(field)) return null;
    const idFieldName = getRelationIdFieldName(field);
    if (!record?.[idFieldName] && !record?.[field.name]) return null;
    return { apiPatch: { [idFieldName]: null }, localPatch: { [idFieldName]: null, [field.name]: null } };
  }
  if (isFieldValueEmpty(field, record?.[field.name])) return null;
  /** @type {unknown} */
  let empty = getEmptyFieldValue(field);
  if (field.type === FIELD_TYPE.CURRENCY) empty = { amountMicros: null, currencyCode: record?.[field.name]?.currencyCode ?? field.settings?.currencyCode ?? 'BRL' };
  if (field.type === FIELD_TYPE.ADDRESS) {
    empty = Object.fromEntries(Object.keys(record?.[field.name] ?? {}).map((key) => [key, key === 'addressLat' || key === 'addressLng' ? null : '']));
  }
  return { apiPatch: { [field.name]: empty }, localPatch: { [field.name]: empty } };
}

/**
 * Alterna um id na seleção.
 * @param {Set<string>} selectedIds @param {string} id
 */
export function toggleSelectedId(selectedIds, id) {
  const next = new Set(selectedIds);
  if (next.has(id)) next.delete(id);
  else next.add(id);
  return next;
}

/**
 * Shift+x: soma à seleção todas as linhas entre a âncora (último x) e a linha focada, inclusive.
 * @param {string[]} orderedIds @param {string | null} anchorId @param {string} targetId @param {Set<string>} selectedIds
 */
export function selectIdRange(orderedIds, anchorId, targetId, selectedIds) {
  const targetIndex = orderedIds.indexOf(targetId);
  const anchorIndex = anchorId ? orderedIds.indexOf(anchorId) : -1;
  if (targetIndex < 0) return selectedIds;
  if (anchorIndex < 0) return new Set([...selectedIds, targetId]);
  const [start, end] = anchorIndex < targetIndex ? [anchorIndex, targetIndex] : [targetIndex, anchorIndex];
  return new Set([...selectedIds, ...orderedIds.slice(start, end + 1)]);
}

/**
 * Muda o campo de agrupamento de vários registros, um por vez: cada um passa pelo diálogo da etapa quando ele
 * pedir algo (data da visita, motivo, campos obrigatórios), e cancelar um card não desfaz os outros.
 * @param {{ records: any[], fieldName: string, value: string | null,
 *   resolve: (record: any, patch: NonNullable<FieldPatch>) => Promise<FieldPatch>,
 *   update: (record: any, patch: NonNullable<FieldPatch>) => Promise<unknown> }} input
 */
export async function moveRecordsToGroup({ records, fieldName, value, resolve, update }) {
  /** @type {{ moved: any[], cancelled: any[], failed: { record: any, error: unknown }[], unchanged: any[] }} */
  const result = { moved: [], cancelled: [], failed: [], unchanged: [] };
  for (const record of records) {
    if ((record[fieldName] ?? null) === value) {
      result.unchanged.push(record);
      continue;
    }
    const resolved = await resolve(record, { apiPatch: { [fieldName]: value }, localPatch: { [fieldName]: value } });
    if (!resolved) {
      result.cancelled.push(record);
      continue;
    }
    try {
      await update(record, resolved);
      result.moved.push(record);
    } catch (error) {
      result.failed.push({ record, error });
    }
  }
  return result;
}
